// Вкладка ленты «Формулы» (как «Уравнение» в Word): вставка, структуры с галереями шаблонов, символы, готовые
// формулы, оформление (номер, «где», перенос), ссылки на формулы, сервис. Шаблон вставляется в формулу,
// которая сейчас правится; если такой нет - создаётся новая формула с этим шаблоном.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { RegistryApi, Ctx, MenuItemSpec } from '../../core/registry';
import { Popover } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { STRUCTS, SYMBOLS, READY, CHEM, UNITS, unitLatex, tplPreview, type TplGroup } from './templates';
import { TplGrid, tplHtml, ChemInput } from './Palette';
import { insertIntoActive, mathUI } from './state';
import { renderTex, toggleKeyboard } from './ml';
import { toPlain } from './latex';
import { numberingStore } from '../../core/editor/numberingPlugin';
import { insertBlocks } from '../../core/editor/insert';
import { NBSP } from '../../core/schema/constants';

/** Вставить шаблон: в правящуюся формулу, иначе - новой формулой (выключной в пустом абзаце, иначе строчной). */
export function applyTemplate(ctx: Ctx, tpl: string) {
  if (insertIntoActive(tpl)) return;
  const ed = ctx.editor;
  if (!ed) return;
  const latex = tplPreview(tpl);
  const $f = ed.state.selection.$from;
  const inText = $f.parent.type.name === 'paragraph' && $f.parent.content.size > 0 && $f.depth > 0;
  if (inText && !/\\begin|\\sum|\\int|\\frac|\\lim|\\prod/.test(tpl)) {
    const at = ed.state.selection.from;
    ed.chain().focus().insertContent({ type: 'mathInline', attrs: { latex } }).run();
    mathUI.requestEdit(at);
  } else {
    const at = insertBlocks(ed, { type: 'formula', attrs: { latex, numbered: true, where: [] } });
    mathUI.requestEdit(at);
  }
}

function useActive() {
  return useSyncExternalStore((f) => mathUI.on(f), () => mathUI.active?.kind || '');
}

/** Кнопка ленты с галереей шаблонов. */
function Drop({ ctx, g, wide, children, label, icon, testid, small }: {
  ctx: Ctx; g?: TplGroup; wide?: number; children?: (close: () => void) => React.ReactNode; label: string; icon: string; testid: string; small?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState<false | 'portal' | 'inline'>(false);
  const close = () => setOpen(false);
  // внутри свёрнутой группы ленты (всплывающее окно) - галерея прямо под кнопкой, иначе окно группы закроется
  const toggle = () => setOpen(open ? false : (ref.current?.closest('.popover') ? 'inline' : 'portal'));
  useEffect(() => {
    if (open !== 'inline') return;
    const down = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) close(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', down, true);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', down, true); document.removeEventListener('keydown', key, true); };
  }, [open]);
  const body = (
    <div className={'mx-pop' + (open === 'inline' ? ' mx-pop-inline' : '')} style={{ width: wide || 460 }}
      onMouseDown={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); }}>
      {g && <>
        <div className="mx-pal-h">{g.title}{g.hint && <span className="muted"> · {g.hint}</span>}</div>
        <TplGrid items={g.items} cols={g.cols || 5} onPick={(t) => { close(); applyTemplate(ctx, t); }} testid={'gal-' + g.id} />
      </>}
      {children?.(close)}
    </div>
  );
  return (
    <span ref={wrap} className="ribbon-menu mx-rb-wrap">
      <button ref={ref} type="button" className={'mx-rb' + (small ? ' mx-rb-small' : '') + (open ? ' is-open' : '')} title={g?.hint ? `${label}: ${g.hint}` : label}
        onMouseDown={(e) => e.preventDefault()} onClick={toggle} data-testid={'mx-' + testid}>
        <span className="mx-rb-ico" dangerouslySetInnerHTML={{ __html: icon.startsWith('<') ? icon : tplHtml(icon) }} />
        <span className="mx-rb-label">{label}</span>
      </button>
      {open === 'portal' && <Popover anchor={ref.current} onClose={close}>{body}</Popover>}
      {open === 'inline' && body}
    </span>
  );
}

/** Галерея одной группы шаблонов внутри всплывающего окна. */
function Gallery({ g, ctx, close }: { g: TplGroup; ctx: Ctx; close: () => void }) {
  return (
    <>
      {g.hint && <div className="muted mx-small" style={{ marginBottom: 4 }}>{g.hint}</div>}
      <TplGrid items={g.items} cols={g.cols || 6} onPick={(t) => { close(); applyTemplate(ctx, t); }} testid={'gal-' + g.id} />
    </>
  );
}

/** Вкладки внутри всплывающей галереи (последняя открытая запоминается). */
function Tabbed({ tabs, pref }: { tabs: { id: string; title: string; body: React.ReactNode }[]; pref: string }) {
  const [cur, setCur] = useState<string>(() => { try { return localStorage.getItem('sto.' + pref) || tabs[0].id; } catch { return tabs[0].id; } });
  const t = tabs.find((x) => x.id === cur) || tabs[0];
  const pick = (id: string) => { setCur(id); try { localStorage.setItem('sto.' + pref, id); } catch { /* */ } };
  return (
    <div className="mx-tabbed">
      <div className="mx-pal-tabs">{tabs.map((x) => (
        <button key={x.id} type="button" className={'mx-pal-tab' + (x.id === t.id ? ' is-active' : '')} onClick={() => pick(x.id)} data-testid={'rbt-' + x.id}>{x.title}</button>
      ))}</div>
      {t.body}
    </div>
  );
}

/** Список нумерованных формул для ссылки (с отрисованными формулами). */
function FormulaRefs({ ctx, close }: { ctx: Ctx; close: () => void }) {
  const nb = numberingStore.get();
  const items = nb.order.map((id) => [id, nb.byId.get(id)!] as const).filter(([, i]) => i && i.kind === 'eq');
  if (!items.length) return <div className="muted pad">В документе пока нет нумерованных формул.</div>;
  return (
    <div className="mx-refs">
      <div className="mx-pal-h">Ссылка на формулу <span className="muted">· «по формуле (N)», номер обновляется сам (4.6.1.6)</span></div>
      {items.map(([id, i]) => (
        <button key={id} type="button" className="mx-ref-item" onClick={() => { close(); insertEqRef(ctx, id); }} data-testid={'menu-eqref-' + id}>
          <span className="mx-ref-num">{i.label}</span>
          <span className="mx-ref-math" dangerouslySetInnerHTML={{ __html: renderTex(i.title || '', false) }} />
        </button>
      ))}
    </div>
  );
}

/** Пункты меню «Ссылка на формулу»: все нумерованные формулы документа. */
function refItems(): MenuItemSpec[] {
  const nb = numberingStore.get();
  const items = nb.order.map((id) => [id, nb.byId.get(id)!] as const).filter(([, i]) => i && i.kind === 'eq');
  if (!items.length) return [{ label: 'Нумерованных формул пока нет', disabled: true }];
  return items.map(([id, i]) => ({ id: 'eqref-' + id, label: `${i.label}  ${toPlain(i.title || '').slice(0, 48)}`, icon: 'formula',
    onClick: (ctx: Ctx) => insertEqRef(ctx, id) }));
}

/** Вставить ссылку на формулу у курсора: «формуле (N)» (слово не повторяется, если уже набрано). */
export function insertEqRef(ctx: Ctx, id: string) {
  const ed = ctx.editor;
  if (!ed) return;
  const $f = ed.state.selection.$from;
  const before = $f.parent.textBetween(Math.max(0, $f.parentOffset - 30), $f.parentOffset, '', '￼');
  const content: any[] = [];
  if (!/формул\S*[\s ]*$/i.test(before)) content.push({ type: 'text', text: (/\S$/.test(before) ? ' ' : '') + 'формуле' + NBSP });
  else if (!/[\s ]$/.test(before)) content.push({ type: 'text', text: NBSP });
  content.push({ type: 'xref', attrs: { target: id } });
  ed.chain().focus().insertContent(content).run();
}

export function setupRibbon(r: RegistryApi) {
  // --- вставка
  r.ribbonGroups.add({ id: 'formulas.insert', tab: 'formulas', title: 'Вставка', order: 10 });
  r.ribbonItems.add({ id: 'formula', group: 'formulas.insert', type: 'split', command: 'math.formula', icon: 'formula', label: 'Формула', order: 1,
    items: [
      { id: 'math-quick-block', label: 'Формула с номером (Alt+=)', command: 'math.quick', arg: 'block', icon: 'formula' },
      { id: 'math-calc', label: 'Строка расчёта без номера', command: 'math.quick', arg: 'calc', icon: 'formula' },
      { id: 'math-quick-inline', label: 'Формула в строке', command: 'math.quick', arg: 'inline', icon: 'sigma' },
      { separator: true },
      { id: 'math-from-text', label: 'Из LaTeX, Word, MathML, текста…', command: 'math.fromText', icon: 'paste' },
      { id: 'math-convert-sel', label: 'Выделенный текст -> формула', command: 'math.convertSelection', icon: 'type' },
      { id: 'math-chem', label: 'Химическая формула…', command: 'math.chem', icon: 'omega' },
    ] });
  r.ribbonItems.add({ id: 'formula-inline', group: 'formulas.insert', type: 'large', command: 'math.inline', label: 'В строке', order: 2 });

  // --- структуры (галереи)
  r.ribbonGroups.add({ id: 'formulas.struct', tab: 'formulas', title: 'Структуры', order: 20 });
  STRUCTS.forEach((g, i) => r.ribbonItems.add({ id: 'st-' + g.id, group: 'formulas.struct', type: 'custom', order: i,
    render: (ctx) => <Drop ctx={ctx} g={g} label={g.title} icon={g.icon} testid={'rb-st-' + g.id} wide={g.cols && g.cols <= 3 ? 520 : 460} /> }));

  // --- символы и готовые формулы: одна кнопка - галерея с вкладками (как «Символы» в Word)
  r.ribbonGroups.add({ id: 'formulas.symbols', tab: 'formulas', title: 'Символы и готовые', order: 30 });
  r.ribbonItems.add({ id: 'sy-all', group: 'formulas.symbols', type: 'custom', order: 1,
    render: (ctx) => <Drop ctx={ctx} label="Символы" icon="\alpha\le\infty" testid="rb-symbols" wide={560}>{(close) => (
      <Tabbed tabs={[
        ...SYMBOLS.map((g) => ({ id: g.id, title: g.title, body: <Gallery g={g} ctx={ctx} close={close} /> })),
        { id: 'units', title: 'Единицы', body: <>
          <div className="mx-pal-h">Единицы величин <span className="muted">· только в строке расчёта (4.6.1.8, 4.6.2.6)</span></div>
          <div className="mx-units">{UNITS.map((u) => <button key={u} type="button" className="mx-unit-btn" onClick={() => { close(); applyTemplate(ctx, unitLatex(u)); }}>{u}</button>)}</div>
        </> },
        { id: 'chem', title: 'Химия', body: <>
          <ChemInput onPick={(l) => { close(); applyTemplate(ctx, l); }} />
          <div className="mx-pal-h">Шаблоны</div>
          <TplGrid items={CHEM} cols={4} onPick={(t) => { close(); applyTemplate(ctx, t); }} />
        </> },
      ]} pref="math.rb.sym" />)}</Drop> });
  r.ribbonItems.add({ id: 'rd-all', group: 'formulas.symbols', type: 'custom', order: 2,
    render: (ctx) => <Drop ctx={ctx} label="Готовые" icon="W(p)" testid="rb-ready" wide={640}>{(close) => (
      <Tabbed tabs={READY.map((g) => ({ id: g.id, title: g.title, body: <Gallery g={g} ctx={ctx} close={close} /> }))} pref="math.rb.ready" />)}</Drop> });

  // --- оформление формулы, ссылка, сервис (одна компактная группа: лента не сворачивается на 1366–1440 px)
  r.ribbonGroups.add({ id: 'formulas.layout', tab: 'formulas', title: 'Формула', order: 50 });
  r.ribbonItems.add({ id: 'formula-num', group: 'formulas.layout', type: 'toggle', command: 'math.toggleNumber', label: 'Номер', order: 1 });
  r.ribbonItems.add({ id: 'formula-where', group: 'formulas.layout', type: 'button', command: 'math.where', label: 'Где…', order: 2 });
  r.ribbonItems.add({ id: 'formula-ref', group: 'formulas.layout', type: 'custom', order: 3,
    render: (ctx) => <Drop ctx={ctx} small label="Ссылка" icon={'<span class="mx-rb-txt">(1)</span>'} testid="rb-formula-ref" wide={420}>{(close) => <FormulaRefs ctx={ctx} close={close} />}</Drop> });
  r.ribbonItems.add({ id: 'formula-kb', group: 'formulas.layout', type: 'custom', order: 4, render: () => <KbButton /> });
  r.ribbonItems.add({ id: 'formula-canon', group: 'formulas.layout', type: 'button', command: 'math.canonAll', label: 'К СТО', order: 5 });
  r.ribbonItems.add({ id: 'formula-help', group: 'formulas.layout', type: 'button', command: 'math.help', label: 'Справка', order: 6 });

  // --- вкладка «Вставка»
  r.ribbonGroups.add({ id: 'insert.math', tab: 'insert', title: 'Формулы', order: 30 });
  r.ribbonItems.add({ id: 'ins-formula', group: 'insert.math', type: 'split', command: 'math.formula', icon: 'formula', label: 'Формула',
    items: [
      { label: 'Формула с номером (Alt+=)', command: 'math.quick', arg: 'block', icon: 'formula' },
      { label: 'Формула в строке', command: 'math.quick', arg: 'inline', icon: 'sigma' },
      { label: 'Из LaTeX, Word, MathML, текста…', command: 'math.fromText', icon: 'paste' },
      { label: 'Химическая формула…', command: 'math.chem', icon: 'omega' },
    ] });
}

function KbButton() {
  const active = useActive();
  return (
    <button type="button" className="btn" title="Экранная клавиатура формул (сначала щёлкните формулу)" disabled={!active}
      onMouseDown={(e) => e.preventDefault()} onClick={() => toggleKeyboard(mathUI.active?.mf)} data-testid="rb-formula-kb">
      <Icon name="keyboard" /><span className="btn-label">Клавиатура</span>
    </button>
  );
}
